import { useCallback, useEffect, useState } from 'preact/hooks';
import { UnorderedList } from '../list';
import { TertiaryButton } from '../button';
import { focusFirstAvailable, formatNumber } from '../../utility';
import type { JSX } from 'preact';
import type { MutableRef } from 'preact/hooks';

const DRAGGING_CLASS = 'dragging';

const handleDragOver = (e: JSX.TargetedEvent<HTMLInputElement>) => {
  e.currentTarget.classList.add(DRAGGING_CLASS);
};

const handleDragLeave = (e: JSX.TargetedEvent<HTMLInputElement>) => {
  e.currentTarget.classList.remove(DRAGGING_CLASS);
};

// prevent file upload cancel from bubbling to other cancelable elements (e.g. <dialog>)
const handleCancel = (e: JSX.TargetedEvent<HTMLInputElement>) => {
  e.stopPropagation();
};

const FILE_SIZES = {
  byte: 1,
  kilobyte: 1000,
  megabyte: 1000 ** 2,
  gigabyte: 1000 ** 3,
  format: function format(size: number) {
    let options;

    if (size < this.kilobyte) {
      options = { divisor: this.byte, unit: 'bytes' };
    } else if (size < this.megabyte) {
      options = { divisor: this.kilobyte, unit: 'KB' };
    } else if (size < this.gigabyte) {
      options = { divisor: this.megabyte, unit: 'MB' };
    } else {
      options = { divisor: this.gigabyte, unit: 'GB' };
    }

    return `${formatNumber(size / options.divisor, 'ToNearestHundredth')} ${options.unit}`;
  },
};

/**
 * `renderList: false` hides the rows but keeps this component MOUNTED, because everything
 * that makes a file field work lives in the `change` listener below — multi-select
 * accumulation, `accept` type checking, `data-max-file-size`, `data-min-files`/`data-max-files`
 * and the `setCustomValidity` messaging. A consumer that wants its own per-file row UI must
 * lose the rows only, never the behaviour.
 */
export default function FileList({
  inputRef,
  renderList = true,
}: {
  inputRef: MutableRef<HTMLInputElement>;
  renderList?: boolean;
}) {
  const [files, setFiles] = useState<FileList>(new DataTransfer().files);

  const handleChange = useCallback((e: JSX.TargetedEvent<HTMLInputElement>) => {
    const { currentTarget } = e;
    const { files, multiple } = currentTarget;
    const isRemoval = e instanceof CustomEvent && e.detail?.action === 'remove';

    if (!isRemoval) {
      setFiles((prev) => {
        const dataTransfer = new DataTransfer();

        if (multiple) {
          // persist previously added file(s)
          Array.from(prev).forEach((file) => {
            dataTransfer.items.add(file);
          });
        }

        // add newly selected file(s)
        Array.from(files).forEach((file) => {
          dataTransfer.items.add(file);
        });

        currentTarget.files = dataTransfer.files;

        return currentTarget.files;
      });
    }

    const iterableFiles = Array.from(currentTarget.files);
    const formField = currentTarget.closest('.form-field');
    const errorMessage = currentTarget.parentElement.querySelector('.error-message') as HTMLElement;
    let errorMessageOverride;

    // validate file type
    const validTypes = currentTarget.accept.split(',').map((type) => {
      let cleanedType = type.trim();

      if (cleanedType.endsWith('/*')) {
        cleanedType = cleanedType.substring(0, cleanedType.length - 1);
      }

      return cleanedType;
    });

    if (validTypes.length) {
      const invalidFileTypes = iterableFiles.filter(
        ({ name, type }) =>
          !validTypes.some((validType) => name.endsWith(validType) || type.includes(validType)),
      );

      if (invalidFileTypes.length) {
        const invalids = Array.from(
          new Set(
            invalidFileTypes.map(({ name, type }) => type || name.substring(name.lastIndexOf('.'))),
          ),
        );
        const isSingular = invalids.length === 1;

        errorMessageOverride = `File ${isSingular ? 'type' : 'types'} of ${invalids.join(', ')} ${
          isSingular ? 'is' : 'are'
        } not allowed.`;
      }
    }

    // validate maxFileSize
    const maxSize = parseInt(currentTarget.dataset.maxFileSize);
    if (!errorMessageOverride && maxSize) {
      const isTooBig = iterableFiles.some((file) => {
        return file.size > maxSize;
      });

      if (isTooBig) {
        errorMessageOverride = `Image size exceeds ${FILE_SIZES.format(
          maxSize,
        )}. Please upload a smaller file.`;
      }
    }

    // validate min/max files
    if (currentTarget.multiple) {
      const minFiles = parseInt(currentTarget.dataset.minFiles);
      const maxFiles = parseInt(currentTarget.dataset.maxFiles);
      const curLength = iterableFiles.length;

      if (!currentTarget.required && !curLength) {
        // skip validation if not required and no files selected
      } else if (minFiles && maxFiles && (curLength < minFiles || curLength > maxFiles)) {
        errorMessageOverride = `Please upload between ${minFiles} and ${maxFiles} files.`;
      } else if (minFiles && curLength < minFiles) {
        errorMessageOverride = `Please upload at least ${minFiles} file${minFiles > 1 ? 's' : ''}.`;
      } else if (maxFiles && curLength > maxFiles) {
        errorMessageOverride = `Please upload at most ${maxFiles} file${maxFiles > 1 ? 's' : ''}.`;
      }
    }

    if (errorMessageOverride) {
      currentTarget.setCustomValidity(errorMessageOverride);
      formField?.classList.add('force-errors-without-submit');
      if (errorMessage) {
        if (!errorMessage?.dataset.originalErrorMessage) {
          errorMessage.dataset.originalErrorMessage = errorMessage.textContent; // store original message
        }
        errorMessage.textContent = currentTarget.validationMessage;
      }
    } else {
      currentTarget.setCustomValidity('');
      formField?.classList.remove('force-errors-without-submit');
      if (errorMessage) {
        errorMessage.textContent =
          errorMessage.dataset.originalErrorMessage ?? errorMessage.textContent; // replace with original message, if available
        delete currentTarget.dataset.originalErrorMessage;
      }
    }

    currentTarget.classList.remove(DRAGGING_CLASS);
    focusFirstAvailable(currentTarget.parentElement, {
      selector: '.file-list button',
      isSync: false,
    });
  }, []);

  useEffect(() => {
    const { current } = inputRef;

    if (current) {
      current.addEventListener('cancel', handleCancel);
      current.addEventListener('change', handleChange);
      current.addEventListener('dragleave', handleDragLeave);
      current.addEventListener('dragover', handleDragOver);
    }

    return () => {
      if (current) {
        current.removeEventListener('cancel', handleCancel);
        current.removeEventListener('change', handleChange);
        current.removeEventListener('dragleave', handleDragLeave);
        current.removeEventListener('dragover', handleDragOver);
      }
    };
  }, [inputRef, handleChange]);

  const handleRemoveFileClick = useCallback(
    (e) => {
      const input = inputRef.current;
      const fileIndex = +e.currentTarget.value;

      const filteredFiles = new DataTransfer();

      for (let i = 0; i < input.files.length; ++i) {
        const file = input.files[i];
        if (file !== input.files[fileIndex]) {
          filteredFiles.items.add(file);
        }
      }

      input.files = filteredFiles.files;

      // force re-validation of file sizes if something too big was selected and then removed
      input.dispatchEvent(
        new CustomEvent('change', { bubbles: true, detail: { action: 'remove' } }),
      );

      setFiles(filteredFiles.files);
    },
    [inputRef],
  );

  if (!renderList || !files?.length) {
    return null;
  }

  return (
    <UnorderedList
      className="file-list"
      marker={{ type: 'icon', value: 'document' }}
      items={Array.from(files).map(({ name, size }, index) => {
        return (
          <>
            {name} ({FILE_SIZES.format(size)})
            <TertiaryButton
              aria-label="Remove file"
              className="remove-file"
              icon="close"
              size="small"
              isIconOnly
              value={index}
              onClick={handleRemoveFileClick}
            />
          </>
        );
      })}
    />
  );
}
